// Chart element takes a chart type generic which says what kind of overrides we can do

import { ElementsProvider, useElements } from './context';

/**
 * Creates an overridable implementation of a component.
 * The overridable version is exposed under static `Component` key.
 * When options.as is provided, it will mark the implementation as replacement for the element provided
 *
 * @public
 */

/** @public */
import { jsx as _jsx } from "react/jsx-runtime";
/** @public */
export function element(DefaultComponent, options) {
  var _options$as;
  if (options === void 0) {
    options = {};
  }
  const displayName = DefaultComponent.displayName || DefaultComponent.name || options.displayName;
  const kind = ((_options$as = options.as) === null || _options$as === void 0 ? void 0 : _options$as.kind) || Symbol(displayName);
  if (options.as && !options.as.kind) {
    throw new Error('Invalid "as" component. It must be an `element` or `element.Component` value.');
  }

  /**
   * When used outside its own slot, it will register the override
   * while when its used inside the slot, it will render the component
   */
  const ElementComponent = outerProps => {
    const ctx = useElements();
    if (!ctx) throw new Error('Element must be used inside an ElementsProvider');

    // Check if we are rendering under the own ElementComponent, if so, just render
    if (ctx !== null && ctx !== void 0 && ctx.isNested(kind)) {
      return /*#__PURE__*/_jsx(DefaultComponent
      // spread default props only if not overriden
      , {
        ...(!options.as && ctx.getProps(kind)),
        ...outerProps
      });
    } else {
      ctx.register(kind, {
        Component: DefaultComponent,
        outerProps
      });
      return null;
    }
  };
  ElementComponent.kind = kind;

  // When as is defined we return the component without any slot implementation
  if (options.as) {
    return ElementComponent;
  }

  /**
   * Defines the slot where the element will be rendered, grabbing the implementation from the context
   */
  const ElementSlot = defaultProps => {
    const ctx = useElements();
    const {
      Component = DefaultComponent,
      outerProps
    } = (ctx === null || ctx === void 0 ? void 0 : ctx.retrieve(kind)) || {};
    const isSelf = Component === DefaultComponent;
    return /*#__PURE__*/_jsx(ElementsProvider, {
      kind: kind,
      defaultProps: defaultProps,
      children: /*#__PURE__*/_jsx(Component
      // spread default props only if not overriden
      , {
        ...(isSelf && defaultProps),
        ...outerProps
      })
    });
  };
  ElementSlot.kind = kind;
  ElementSlot.Component = ElementComponent;
  ElementSlot.displayName = `element(${displayName})`;
  return ElementSlot;
}